import { FC, useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import {
  Box,
  Button,
  ColumnLayout,
  Container,
  ContentLayout,
  FormField,
  Header,
  Multiselect,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';

import { deleteItem as deleteInv } from '../api/generic';
import ErrorBar from '../components/ErrorBar';
import { Space } from '../components/Flex';
import { useAuthContext } from '../contexts/AuthContext';
import useSubscribe from '../hooks/useSubscribe';
import { InventoryItem } from '../models/Inventory';
import { addFavoriteToUser, FavoriteType, removeFavoriteFromUser } from '../utils/favorite';
import { prefixCollection } from '../utils/prefix';
import { sortName } from '../utils/sort';

const Inventory: FC = () => {
  const { userObject, tenant, tier, currency } = useAuthContext();
  const navigate = useNavigate();
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [error, setError] = useState<Error>();

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [searchParams] = useSearchParams();
  const itemId = searchParams.get('itemId');

  const sortInventory = useCallback(
    (a: InventoryItem, b: InventoryItem) => {
      if (a.id === itemId) return -1;
      if (b.id === itemId) return 1;

      if (!userObject) return sortName(a, b);

      const aFavorite = userObject.favorites?.inventory.includes(a.id);
      const bFavorite = userObject.favorites?.inventory.includes(b.id);

      if (aFavorite && !bFavorite) return -1;
      if (!aFavorite && bFavorite) return 1;

      return sortName(a, b);
    },
    [userObject, itemId],
  );

  const allCategories = Array.from(new Set(inventory.flatMap((item) => item.categories ?? []))).sort();

  useSubscribe<InventoryItem>(prefixCollection('inventory', tenant), setInventory, setError, sortInventory);

  const deleteItem = async (item: InventoryItem) => {
    const confirmed = window.confirm(`Are you sure you want to delete ${item.name}?`);
    if (!confirmed) return;
    await deleteInv(prefixCollection('inventory', tenant), item.id);
  };

  const formatCapacityL = (cap?: number) => {
    if (cap == null) return '';
    return `${cap.toFixed(3).replace(/\.?0+$/, '')} L`;
  };

  const filteredItems = useMemo(
    () =>
      selectedCategories.length > 0
        ? inventory.filter((item) => item.categories?.some((cat) => selectedCategories.includes(cat)))
        : inventory,
    [inventory, selectedCategories],
  );

  const kpis = useMemo(() => {
    const totalItems = filteredItems.length;
    const uniqueCategories = new Set(filteredItems.flatMap((i) => i.categories ?? [])).size;
    const totalValue = filteredItems.reduce((sum, i) => sum + (i.price ?? 0) * (i.quantity ?? 0), 0);

    return { totalItems, uniqueCategories, totalValue };
  }, [filteredItems]);

  return (
    <ContentLayout
      disableOverlap
      header={
        <Header
          variant="h1"
          description={'Hint: You can add items and set a quantity later'}
          actions={
            <SpaceBetween size="m" direction="horizontal">
              <Button iconName="add-plus" variant="primary" onClick={() => navigate('/Inventory/new')}>
                Add ingredient
              </Button>
              {tier >= 3 && (
                <Button iconName="search" variant="normal" onClick={() => navigate('/inventoryCheck')}>
                  Inventory check
                </Button>
              )}
            </SpaceBetween>
          }
        >
          Inventory
        </Header>
      }
    >
      <div style={{ marginTop: 10 }}>
        <Container
          header={<Header variant="h2">Key figures</Header>}
          footer={<Box variant="awsui-key-label">KPIs update with your category filter.</Box>}
        >
          <ColumnLayout columns={3} variant="text-grid">
            <Box>
              <Box variant="awsui-value-large">{kpis.totalItems}</Box>
              <Box variant="awsui-key-label">Items in view</Box>
            </Box>

            <Box>
              <Box variant="awsui-value-large">{kpis.uniqueCategories}</Box>
              <Box variant="awsui-key-label">Unique categories</Box>
            </Box>

            <Box>
              <Box variant="awsui-value-large">{currency(kpis.totalValue.toFixed(2))}</Box>
              <Box variant="awsui-key-label">Inventory value (view)</Box>
            </Box>
          </ColumnLayout>
        </Container>

        <Space h={20} />

        <ErrorBar error={error} setError={setError} />

        <FormField label="Filter by categories">
          <Multiselect
            selectedOptions={selectedCategories.map((value) => ({
              label: value,
              value,
            }))}
            onChange={({ detail }) =>
              setSelectedCategories(
                detail.selectedOptions.map((o) => o.value).filter((v): v is string => typeof v === 'string'),
              )
            }
            options={allCategories.map((cat) => ({ value: cat, label: cat }))}
            placeholder="Select category tags"
            selectedAriaLabel="Selected"
            filteringType="auto"
          />
        </FormField>
        <Space h={20} />
        <Table
          variant="embedded"
          items={
            selectedCategories.length > 0
              ? inventory.filter((item) => item.categories?.some((cat) => selectedCategories.includes(cat)))
              : inventory
          }
          columnDefinitions={[
            {
              header: '',
              hasDynamicContent: true,
              cell: (item) => (
                <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                  {item.id === itemId && <Button iconName="star-filled" variant="inline-link" />}
                  <Button
                    iconName={(userObject?.favorites?.inventory ?? []).includes(item.id) ? 'heart-filled' : 'heart'}
                    variant="inline-link"
                    onClick={() => {
                      const isFav = (userObject?.favorites?.inventory ?? []).includes(item.id);
                      const fn = isFav ? removeFavoriteFromUser : addFavoriteToUser;
                      fn(FavoriteType.INVENTORY, item.id, userObject?.id ?? '');
                    }}
                  />
                </div>
              ),
              width: 32,
            },
            {
              header: 'Name',
              cell: (item) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                  <Link to={`/Inventory/${item.id}`}>{item.name}</Link>
                </span>
              ),
              isRowHeader: true,
              width: '26%',
            },
            {
              header: 'Categories',
              hasDynamicContent: true,
              cell: (item) => (
                <SpaceBetween direction="horizontal" size="xxs">
                  {(item.categories ?? []).map((cat) => {
                    const isSelected = selectedCategories.includes(cat);
                    return (
                      <Button
                        key={cat}
                        variant={isSelected ? 'normal' : 'link'}
                        onClick={() => {
                          setSelectedCategories((prev) =>
                            isSelected ? prev.filter((c) => c !== cat) : [...prev, cat],
                          );
                        }}
                      >
                        <span style={{ whiteSpace: 'nowrap' }}>{cat}</span>
                      </Button>
                    );
                  })}
                </SpaceBetween>
              ),
              width: '18%',
            },
            {
              header: 'Container (size)',
              cell: (item) => (item.isContainer ? <>✅ {formatCapacityL(item.capacityL)}</> : '—'),
              width: '12%',
            },
            {
              header: 'Per container',
              cell: (item) => (item.perContainer ? '✅' : '—'),
              width: '10%',
            },
            {
              header: 'Brix',
              cell: (item) => item.sugarContentPer100g?.toFixed(1),
              width: '6%',
            },
            {
              header: 'Quantity',
              cell: (item) => (
                <>
                  {item.quantity} {item.unit}
                </>
              ),
              width: '8%',
            },
            {
              header: 'Price per unit',
              cell: (item) => (
                <>
                  {currency(item.price ?? 0.0)}/{item.unit}
                </>
              ),
              width: '8%',
            },
            {
              header: 'Value',
              cell: (item) => <>{currency(((item.price ?? 0) * item.quantity).toFixed(2))}</>,
              width: '8%',
            },
            {
              header: 'Actions',
              cell: (item) => <Button iconName="remove" variant="inline-link" onClick={() => deleteItem(item)} />,
              width: '6%',
            },
          ]}
        />
      </div>
    </ContentLayout>
  );
};

export default Inventory;
